// ProductTableModalRB.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";

/**
 * ProductTableModalRB
 * Props:
 *  - show: boolean
 *  - rows: array of {
 *      id, name, brand, verified, status, location,
 *      mrp, batch, stockPacks, loose, expiry
 *    }
 *  - onHide: () => void
 *  - onSelect?: (row) => void
 */
export default function ProductTableModalRB({ show, rows = [], onHide, onSelect }) {
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    if (show) {
      setQuery("");
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [show]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(p =>
      [
        p.name, p.brand, p.status, p.location, p.batch,
        String(p.mrp), String(p.stockPacks)
      ]
        .filter(Boolean)
        .some(s => String(s).toLowerCase().includes(q))
    );
  }, [rows, query]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Select Product</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Control
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, brand, status, location, batch, or MRP"
          className="mb-3"
        />

        <div style={{ maxHeight: 420, overflow: "auto", borderRadius: 8 }}>
          <Table hover responsive className="align-middle mb-0">
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: "var(--bs-body-bg)",
              }}
            >
              <tr>
                <th>Product Name</th>
                <th>Status</th>
                <th>Location</th>
                <th>MRP Rate</th>
                <th>Batch</th>
                <th>Stock Qty</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  role="button"
                  onClick={() => onSelect?.(row)}
                  onKeyDown={(e) => e.key === "Enter" && onSelect?.(row)}
                  tabIndex={0}
                >
                  {/* Product Name */}
                  <td style={{ minWidth: 240 }}>
                    <div className="d-flex align-items-center gap-2">
                      {row.verified ? (
                        <Badge bg="success" pill>Verified</Badge>
                      ) : null}
                      <div className="text-truncate">
                        <div className="fw-semibold">{row.name}</div>
                        <div className="text-muted small">{row.brand}</div>
                        {row.expiry ? (
                          <div className="text-secondary small">Exp: {row.expiry}</div>
                        ) : null}
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td style={{ minWidth: 120 }}>
                    <Badge
                      bg={
                        row.status === "In Stock"
                          ? "success"
                          : row.status === "Low Stock"
                          ? "warning"
                          : "secondary"
                      }
                    >
                      {row.status}
                    </Badge>
                  </td>

                  {/* Location */}
                  <td style={{ minWidth: 120 }}>{row.location}</td>

                  {/* MRP Rate */}
                  <td style={{ minWidth: 120 }}>₹{Number(row.mrp).toFixed(2)}</td>

                  {/* Batch */}
                  <td style={{ minWidth: 80, textAlign: "center" }}>{row.batch}</td>

                  {/* Stock Qty */}
                  <td style={{ minWidth: 160 }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold">{row.stockPacks} Packs</div>
                        <div className="text-muted small">Loose : {row.loose ?? 0}</div>
                      </div>
                      <span className="text-muted">›</span>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No products match your search
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>

    </Modal>
  );
}
